import React, { useEffect, useState } from 'react';
import { mockTransactionsData, fetchTransactionsSummary, fetchTransactions } from '../data/mockData';
import EthAmount from '../components/EthAmount';
import './Transactions.css';

const Transactions: React.FC = () => {
  const [summary, setSummary] = useState(mockTransactionsData.summary);
  const [details, setDetails] = useState(mockTransactionsData.details);
  const [pagination, setPagination] = useState(mockTransactionsData.pagination);

  useEffect(() => {
    let mounted = true;
    const loadSummary = async () => {
      try {
        const apiSummary = await fetchTransactionsSummary();
        if (mounted) setSummary(apiSummary);
      } catch {}
    };
    const loadDetails = async () => {
      try {
        const { details: d, pagination: p } = await fetchTransactions(1, 10);
        if (mounted) {
          setDetails(d);
          setPagination(p);
        }
      } catch {}
    };
    loadSummary();
    loadDetails();
    return () => { mounted = false; };
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePageChange = (page: number) => {
    (async () => {
      const { details: d, pagination: p } = await fetchTransactions(page, 10);
      setDetails(d);
      setPagination(p);
    })();
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const { currentPage, totalPages } = pagination;

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &lt;
      </button>
    );

    // Page numbers
    if (currentPage > 3) {
      buttons.push(
        <button key="1" className="pagination-btn" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
    }

    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - 2) {
      buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      buttons.push(
        <button key={totalPages} className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &gt;
      </button>
    );

    return buttons;
  };

  return (
    <div className="transactions-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="title-icon transactions"></span>
          User Transactions
        </h1>
      </div>

      <div className="content">
        {/* Summary Section */}
        <div className="summary-section">
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-title">Bought</div>
              <div className="card-value">
                <EthAmount amount={summary.bought} />
              </div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Sold</div>
              <div className="card-value">
                <EthAmount amount={summary.sold} />
              </div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Profit & Loss</div>
              <div className="card-value green-text">
                <EthAmount amount={summary.profitAndLoss} />
              </div>
            </div>
          </div>

        </div>

        {/* Detail Table */}
        <div className="detail-section">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    Date
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    Type
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="currency-icon">$</span>
                    USD
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    ETH
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    Token
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    Token Price
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    Maker
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    Txn
                    <span className="sort-icon"></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {details.map((transaction, index) => (
                  <tr key={index}>
                    <td>{transaction.date}</td>
                    <td>
                      <span className={`type-badge ${transaction.type.toLowerCase()}`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td>{transaction.usd}</td>
                    <td>{transaction.eth}</td>
                    <td>{transaction.token.toLocaleString()}</td>
                    <td>{transaction.tokenPrice}</td>
                    <td>
                      <div className="address-cell">
                        {transaction.maker}
                        <span 
                          className="copy-icon"
                          onClick={() => handleCopy(transaction.maker)}
                          title="Copy address"
                        >
                          📋
                        </span>
                      </div>
                    </td>
                    <td>
                      <a href={transaction.txnLink} className="txn-link" target="_blank" rel="noopener noreferrer">
                        🔗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Total {pagination.totalItems} items
            </div>
            <div className="pagination-controls">
              {renderPaginationButtons()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
